import {
  CheckCircleOutlined, ExperimentOutlined, KeyOutlined, ReloadOutlined
} from "@ant-design/icons";
import { Alert, Button, Card, Col, DatePicker, Form, Input, InputNumber, Row, Space, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import {
  formatCurrency, formatDateTime, formatGroupedInput, formatMileage, formatPhoneInput,
  normalizeVinInput, parseGroupedInput
} from "../../shared/utils/formatters";

const DEFAULTS = {
  vin: "1HGCM82633A004352",
  phone: "(415) 555-0123",
  amount: 1234.56,
  mileage: 128520
};

export function InputStandardsTestPage() {
  const { text } = useAppConfig();
  const [vin, setVin] = useState(DEFAULTS.vin);
  const [phone, setPhone] = useState(DEFAULTS.phone);
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [amount, setAmount] = useState(DEFAULTS.amount);
  const [mileage, setMileage] = useState(DEFAULTS.mileage);
  const [submittedAt, setSubmittedAt] = useState<string>();
  const submitTest = () => setSubmittedAt(new Date().toISOString());

  const reset = () => {
    setVin(DEFAULTS.vin);
    setPhone(DEFAULTS.phone);
    setDate(dayjs());
    setAmount(DEFAULTS.amount);
    setMileage(DEFAULTS.mileage);
    setSubmittedAt(undefined);
  };

  const vinValid = /^[A-HJ-NPR-Z0-9]{17}$/.test(vin);
  const phoneValid = phone.replace(/\D/g, "").length >= 10;

  return <section className="qa-test-page">
    <div className="page-title business-page-title">
      <div>
        <Typography.Text type="secondary">{text.qaTools}</Typography.Text>
        <Typography.Title level={2}>{text.inputStandardsTest}</Typography.Title>
        <Typography.Paragraph type="secondary">{text.inputStandardsHint}</Typography.Paragraph>
      </div>
      <Tag icon={<ExperimentOutlined />} color="blue">{text.qaOnly}</Tag>
    </div>

    <Alert
      showIcon
      type="info"
      icon={<KeyOutlined />}
      message={text.keyboardTestHint}
      className="qa-test-alert"
    />

    <form onSubmit={event => { event.preventDefault(); submitTest(); }}>
      <Row gutter={[18, 18]} className="qa-standard-grid">
        <Col xs={24} lg={12}>
          <StandardCard title={text.vinStandard} valid={vinValid} text={text}>
            <Form.Item label={text.vin} htmlFor="qa-vin" validateStatus={vinValid ? "success" : "error"} help={text.vinStandardHelp}>
              <Input
                id="qa-vin"
                className="mono"
                value={vin}
                maxLength={17}
                autoCapitalize="characters"
                spellCheck={false}
                onChange={event => setVin(normalizeVinInput(event.target.value))}
                onPressEnter={submitTest}
              />
            </Form.Item>
            <ResultLine label={text.actualValue} value={vin || "—"} />
          </StandardCard>
        </Col>

        <Col xs={24} lg={12}>
          <StandardCard title={text.phoneStandard} valid={phoneValid} text={text}>
            <Form.Item label={text.phone} htmlFor="qa-phone" validateStatus={phoneValid ? "success" : "error"} help={text.phoneStandardHelp}>
              <Input
                id="qa-phone"
                inputMode="tel"
                autoComplete="tel"
                value={phone}
                onChange={event => setPhone(formatPhoneInput(event.target.value))}
                onPressEnter={submitTest}
              />
            </Form.Item>
            <ResultLine label={text.actualValue} value={phone || "—"} />
          </StandardCard>
        </Col>

        <Col xs={24} lg={12}>
          <StandardCard title={text.dateStandard} valid={Boolean(date)} text={text}>
            <Form.Item label={text.dateAndTime} htmlFor="qa-date" help="MM/DD/YYYY hh:mm AM/PM">
              <DatePicker
                id="qa-date"
                showTime={{ format: "hh:mm A" }}
                format="MM/DD/YYYY hh:mm A"
                value={date}
                onChange={setDate}
                onKeyDown={event => { if (event.key === "Enter") submitTest(); }}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <ResultLine label={text.actualValue} value={date ? formatDateTime(date.toDate()) : "—"} />
          </StandardCard>
        </Col>

        <Col xs={24} lg={12}>
          <StandardCard title={text.currencyStandard} valid={amount >= 0} text={text}>
            <Form.Item label={text.currencyAmount} htmlFor="qa-currency" help="$1,234.56">
              <InputNumber
                id="qa-currency"
                min={0}
                precision={2}
                addonBefore="$"
                formatter={formatGroupedInput}
                parser={parseGroupedInput}
                value={amount}
                onChange={value => setAmount(value ?? 0)}
                onPressEnter={submitTest}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <ResultLine label={text.actualValue} value={formatCurrency(amount)} />
          </StandardCard>
        </Col>

        <Col xs={24} lg={12}>
          <StandardCard title={text.mileageStandard} valid={mileage >= 0} text={text}>
            <Form.Item label={text.mileage} htmlFor="qa-mileage" help="128,520">
              <InputNumber
                id="qa-mileage"
                min={0}
                precision={0}
                formatter={formatGroupedInput}
                parser={parseGroupedInput}
                value={mileage}
                onChange={value => setMileage(value ?? 0)}
                onPressEnter={submitTest}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <ResultLine label={text.actualValue} value={formatMileage(mileage)} />
          </StandardCard>
        </Col>

        <Col xs={24} lg={12}>
          <StandardCard title={text.roNumberStandard} valid text={text}>
            <Typography.Paragraph type="secondary">{text.roNumberAutomatic}</Typography.Paragraph>
            <ResultLine label={text.generatedFormat} value="RO-YYYYMMDD-######" />
            <ResultLine label={text.generationOwner} value="API / PostgreSQL" />
          </StandardCard>
        </Col>
      </Row>

      <Card className="qa-submit-card">
        <Space wrap>
          <Button type="primary" htmlType="submit" icon={<KeyOutlined />}>{text.testKeyboardSubmit}</Button>
          <Button type="default" icon={<ReloadOutlined />} onClick={reset}>{text.resetTestValues}</Button>
          {submittedAt && <Tag icon={<CheckCircleOutlined />} color="success">{text.keyboardSubmitPassed} · {formatDateTime(submittedAt)}</Tag>}
        </Space>
      </Card>
    </form>
  </section>;
}

function StandardCard({ title, valid, text, children }: {
  title: string;
  valid: boolean;
  text: Record<string, string>;
  children: React.ReactNode;
}) {
  return <Card
    className="qa-standard-card"
    title={title}
    extra={<Tag color={valid ? "success" : "error"}>{valid ? text.pass : text.fail}</Tag>}
  >{children}</Card>;
}

function ResultLine({ label, value }: { label: string; value: string }) {
  return <div className="qa-result-line"><span>{label}</span><b>{value}</b></div>;
}
